import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";

const formSchema = z.object({
  category: z.string().min(1, "Categoria é obrigatória"),
  type: z.string().min(1, "Tipo é obrigatório"),
  material: z.string().min(1, "Material é obrigatório"),
  thickness: z.string().min(1, "Espessura é obrigatória"),
  dimension: z.string().min(1, "Dimensão é obrigatória"),
  quantity: z.string().min(1, "Quantidade é obrigatória"),
  description: z.string().optional(),
});

const categories = [
  "Barras Metálicas",
  "Chapas de Aço",
  "Tubos Metálicos",
  "Telas e Grades",
];

const typesByCategory = {
  "Barras Metálicas": ["Metalons", "Barras chatas", "Barras redondas"],
  "Chapas de Aço": ["Chapas lisas", "Chapas xadrez"],
  "Tubos Metálicos": ["Tubos redondos", "Tubos quadrados"],
  "Telas e Grades": ["Telas soldadas", "Grades expandidas"],
};

const materials = ["Aço carbono", "Alumínio", "Inox"];
const thicknesses = ["1,2 mm", "2 mm", "3 mm", "4 mm", "5 mm"];

interface StockFormProps {
  onClose: () => void;
}

export function StockForm({ onClose }: StockFormProps) {
  const { toast } = useToast();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      category: "",
      type: "",
      material: "",
      thickness: "",
      dimension: "",
      quantity: "",
      description: "",
    },
  });

  const selectedCategory = form.watch("category");

  function onSubmit(values: z.infer<typeof formSchema>) {
    // Here you would typically save to your backend
    console.log(values);
    toast({
      title: "Item cadastrado com sucesso!",
      description: "O item foi adicionado ao estoque.",
    });
    onClose();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Categoria</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de Item</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um tipo" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {selectedCategory &&
                    typesByCategory[selectedCategory as keyof typeof typesByCategory].map(
                      (type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      )
                    )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="material"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Material</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um material" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {materials.map((material) => (
                    <SelectItem key={material} value={material}>
                      {material}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="thickness"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Espessura</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma espessura" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {thicknesses.map((thickness) => (
                    <SelectItem key={thickness} value={thickness}>
                      {thickness}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="dimension"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dimensão/Comprimento</FormLabel>
              <FormControl>
                <Input placeholder="Ex: 6m ou 1200x2400mm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="quantity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Quantidade</FormLabel>
              <FormControl>
                <Input type="number" placeholder="Quantidade inicial" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descrição Adicional</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Observações sobre o item"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">Salvar</Button>
        </div>
      </form>
    </Form>
  );
}